import { defineStore } from "pinia";
import { ref } from "vue";
import Cache from "@/services/Cache";
import Emitter from "@/services/Emitter";
import ApiService from "@/services/ApiService";
import VersionCheckService from "@/services/VersionCheckService";
import WebSocketService from "@/services/WebSocketService";
import TokenHelper from "@/services/TokenHelper";
import type { SessionModel } from "@/models/AccountModels";

interface AccountData {
    bearer: string | null;
    user: SessionModel | null;
}

// Keepalive interval in milliseconds (20 seconds)
const KEEPALIVE_INTERVAL_MS = 20 * 1000;
// Minimum time between server keepalive calls (30 seconds)
const KEEPALIVE_SERVER_THRESHOLD_SECONDS = 30;

// Debug: Track store instances
let storeInstanceCount = 0;

export const useAppStore = defineStore("app", () => 
{
    const instanceId = ++storeInstanceCount;
    console.warn(`🏪 AppStore instance created #${instanceId}`, new Error().stack);
    const account = ref<AccountData>({
        bearer: null,
        user: null
    });
    const isInitialized = ref<boolean>(false);
    const keepaliveTimerId = ref<ReturnType<typeof setTimeout> | null>(null);
    const impersonatedName = ref<string | null>(null);
    let initPromise: Promise<void> | null = null;

    const init = async (): Promise<void> => {
        console.debug(`📍 init() called on instance #${instanceId}, isInitialized=${isInitialized.value}`);
        if (isInitialized.value) {
            console.debug(`⏭️ init() skipped - already initialized on instance #${instanceId}`);
            return;
        }
        if (initPromise) {
            console.debug(`⏭️ init() already in progress on instance #${instanceId}, waiting...`);
            return initPromise;
        }
        initPromise = (async () => {
            // Check cache for existing account data
            const tok: string | undefined = await Cache.get("bearer");
            const usr: SessionModel | undefined = await Cache.get("radar_user");

            if (tok && usr) {
                account.value.bearer = tok;
                account.value.user = usr;
                // Connect WebSocket for existing session (page refresh)
                await connectWebSocket();
            }

            const cachedName: string | undefined = await Cache.get("impersonated_name");
            if (cachedName) {
                impersonatedName.value = cachedName;
            }
            
            isInitialized.value = true;
            console.debug(`✅ init() complete on instance #${instanceId}`);
            
            // Start the keepalive loop
            startKeepalive();
        })();
        return initPromise;
    };

    // Non Reactive Version 
    const getAccount = async (): Promise<AccountData> =>
    {
        await init();
        
        return {
            bearer: account.value.bearer,
            user: account.value.user
        };
    };

    const setAccount = async (rs: SessionModel | null): Promise<void> =>
    {
        if (rs && rs.Token) 
        {
            account.value.bearer = rs.Token;
            account.value.user = rs;

            // set in the cache too ...
            await Cache.set("bearer", account.value.bearer);
            await Cache.set("radar_user", account.value.user);

            // Connect WebSocket for new login
            await connectWebSocket();
        }

        else {

            account.value.bearer = "";
            account.value.user = null;

            await Cache.set("bearer", account.value.bearer);
            await Cache.set("radar_user", account.value.user);

            // Disconnect WebSocket on logout
            await disconnectWebSocket();
        }
    };

    /**
     * Keepalive function that periodically refreshes the JWT token.
     * Only calls the server if enough time has passed since the last call.
     */
    const keepalive = async (): Promise<void> => {
        const bearer = account.value.bearer || await Cache.get("bearer");
        
        if (bearer) {
            const lastValue = await Cache.get("alive");
            const last = lastValue ? new Date(lastValue) : new Date();
            const now = new Date();
            await Cache.set("alive", now.toISOString());
            const diffSeconds = (now.getTime() - last.getTime()) / 1000;
            
            console.debug(`keepalive client check [instance #${instanceId}]`, diffSeconds);
            
            if (diffSeconds > KEEPALIVE_SERVER_THRESHOLD_SECONDS) {
                const isImpersonating = !!impersonatedName.value || !!(await Cache.get("original_bearer"));
                if (isImpersonating) {
                    console.debug("keepalive skipped — active impersonation session");
                } else {
                    console.debug("keepalive server call", diffSeconds);
                    try {
                        const response = await ApiService.refreshToken(true);
                        if (response && response.Token) {
                            await setAccount(response);
                            await Cache.set("alive", new Date().toISOString());
                        }
                    } catch (error) {
                        console.error("Keepalive error:", error);
                    }
                }
            }
        }
        
        // Schedule next keepalive
        keepaliveTimerId.value = setTimeout(() => {
            console.debug(`⏰ Keepalive timer fired on instance #${instanceId}`);
            keepalive();
        }, KEEPALIVE_INTERVAL_MS);
        console.debug(`⏲️ Next keepalive scheduled on instance #${instanceId}, timerId=${keepaliveTimerId.value}`);
    };

    /**
     * Start the keepalive loop
     */
    const startKeepalive = (): void => {
        console.debug(`🔄 startKeepalive() called on instance #${instanceId}, timerId=${keepaliveTimerId.value}`);
        if (keepaliveTimerId.value) {
            console.warn(`⚠️ Keepalive already running on instance #${instanceId}, timer=${keepaliveTimerId.value}`);
            return; // Already running
        }
        console.warn(`▶️ Starting keepalive loop on instance #${instanceId}`, new Error().stack);
        keepalive();
        
        // Version check service disabled - replaced with manual "Refresh App" link in sidebar
        // VersionCheckService.start();
    };

    /**
     * Stop the keepalive loop
     */
    const stopKeepalive = (): void => {
        console.debug(`🛑 stopKeepalive() called on instance #${instanceId}, timerId=${keepaliveTimerId.value}`);
        if (keepaliveTimerId.value) {
            clearTimeout(keepaliveTimerId.value);
            keepaliveTimerId.value = null;
            console.warn(`⏹️ Stopped keepalive loop on instance #${instanceId}`);
        }
        
        // Stop version check service
        VersionCheckService.stop();
    };

    const refresh = async (): Promise<void> => {
        // Clear cached data and re-initialize
        await Cache.unset("bearer");
        await Cache.unset("radar_user");
        account.value.bearer = null;
        account.value.user = null;
        isInitialized.value = false;
        
        // Stop keepalive when logged out
        stopKeepalive();

        // Disconnect WebSocket when logged out
        await disconnectWebSocket();
    };

    /**
     * Connect WebSocket for real-time notifications
     */
    const connectWebSocket = async (): Promise<void> => {
        try {
            const token = account.value.bearer;
            if (token) {
                await WebSocketService.connect(token);
            }
        } catch (error) {
            console.error("Failed to connect WebSocket:", error);
            // Non-blocking - app should work without WebSocket
        }
    };

    /**
     * Disconnect WebSocket
     */
    const disconnectWebSocket = async (): Promise<void> => {
        try {
            await WebSocketService.disconnect();
        } catch (error) {
            console.error("Error disconnecting WebSocket:", error);
        }
    };

    /**
     * Refresh the session by fetching fresh user data from the database.
     * This updates the JWT token with the latest roles/claims.
     * Call this after profile or role updates.
     */
    const refreshSession = async (): Promise<boolean> => {
        try {
            const response = await ApiService.refreshToken(true);
            if (response && response.Token) {
                await setAccount(response);
                console.debug("Session refreshed successfully with new token");
                return true;
            }
            console.warn("Failed to refresh session - no token in response");
            return false;
        } catch (error) {
            console.error("Error refreshing session:", error);
            return false;
        }
    };

    /**
     * Begin impersonating a technician.
     * Saves the current token as original_bearer and replaces the bearer with the impersonation token.
     */
    const setImpersonation = async (token: string, name: string): Promise<void> => {
        const currentBearer = account.value.bearer || await Cache.get("bearer");
        if (currentBearer) {
            await Cache.set("original_bearer", currentBearer);
        }

        // Save current radar_user so it can be restored when impersonation ends
        const currentUser = account.value.user || await Cache.get("radar_user");
        if (currentUser) {
            await Cache.set("original_user", currentUser);
        }

        // Decode impersonated session from token and update account.user
        const impersonatedSession = TokenHelper.decodeSessionFromToken(token);
        if (impersonatedSession) {
            const newUser = { ...impersonatedSession, Token: token, Name: name } as SessionModel;
            account.value.user = newUser;
            await Cache.set("radar_user", newUser);
        }

        account.value.bearer = token;
        impersonatedName.value = name;
        await Cache.set("bearer", token);
        await Cache.set("impersonated_name", name);
        // Reconnect WebSocket with impersonation token
        await connectWebSocket();
    };

    /**
     * End impersonation and restore the original admin token.
     */
    const clearImpersonation = async (): Promise<void> => {
        const originalBearer: string | undefined = await Cache.get("original_bearer");
        if (originalBearer) {
            account.value.bearer = originalBearer;
            await Cache.set("bearer", originalBearer);
            await Cache.unset("original_bearer");
        }

        // Restore original radar_user
        const originalUser: SessionModel | undefined = await Cache.get("original_user");
        if (originalUser) {
            account.value.user = originalUser;
            await Cache.set("radar_user", originalUser);
            await Cache.unset("original_user");
        }

        impersonatedName.value = null;
        await Cache.unset("impersonated_name");
        // Reconnect WebSocket with original token
        await connectWebSocket();
    };

    /**
     * Check if the original user (who started impersonation) has admin privileges.
     * This allows us to determine if the current impersonating user can switch to other techs.
     */
    const canOriginalUserImpersonate = async (): Promise<boolean> => {
        const originalUser: SessionModel | undefined = await Cache.get("original_user");
        if (!originalUser) return false;
        
        // Check for admin role
        const roles = originalUser.Roles;
        if (roles) {
            const hasAdminRole = Object.values(roles).some((role: any) => 
                role?.Name?.toLowerCase() === 'admin' || 
                role?.ClaimValue?.toLowerCase() === 'sysadmin'
            );
            if (hasAdminRole) return true;
        }
        
        // Check for admin claim
        const claims = originalUser.Claims;
        if (claims) {
            const hasAdminClaim = Object.values(claims).some((claim: any) => 
                claim?.ClaimType?.toLowerCase() === 'admin' && 
                claim?.ClaimValue?.toLowerCase() === 'sysadmin'
            );
            if (hasAdminClaim) return true;
        }
        
        return false;
    };

    // Register refresh event listener
    Emitter.on("refresh:appStore", async () => {
        console.debug("refresh:appStore event received");
        await refresh();
    });

    // Register session refresh event listener (for after profile/role updates)
    Emitter.on("refresh:session", async () => {
        console.debug("refresh:session event received");
        await refreshSession();
    });

    // Auto-initialize on store creation
    init();

    return { account, getAccount, setAccount, refresh, refreshSession, impersonatedName, setImpersonation, clearImpersonation, canOriginalUserImpersonate }

});
